import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { 
  Users, 
  FileText, 
  Search,
  CheckCircle,
  Clock,
  XCircle,
  Eye,
  Signature,
  AlertTriangle,
  User,
  Phone,
  Mail,
  MapPin,
  Calendar
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function HoldingWorkers() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [workers, setWorkers] = useState([]);
  const [pendingContracts, setPendingContracts] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedContract, setSelectedContract] = useState(null);
  const [signDialogOpen, setSignDialogOpen] = useState(false);
  const [signing, setSigning] = useState(false);
  const [signatureData, setSignatureData] = useState(null);
  const [activeTab, setActiveTab] = useState("approvals");
  const [approving, setApproving] = useState(null);

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch pending employment contracts
      const contractsRes = await axios.get(`${API_URL}/api/employment-contracts/admin/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingContracts(Array.isArray(contractsRes.data) ? contractsRes.data : []);
      
      // Fetch all workers
      const workersRes = await axios.get(`${API_URL}/api/workers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const allWorkers = Array.isArray(workersRes.data) ? workersRes.data : [];
      setWorkers(allWorkers);
      
      // Filter pending approvals (workers/TLs not yet approved)
      setPendingApprovals(allWorkers.filter(w => !w.is_approved));
      
    } catch (error) {
      console.error("Error fetching data:", error);
      // Don't show error if it's just empty data
      if (error.response?.status !== 404) {
        toast.error("Kunne ikke laste data");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApproveWorker = async (userId, workerName) => {
    setApproving(userId);
    try {
      await axios.put(`${API_URL}/api/workers/${userId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${workerName} er godkjent!`);
      fetchData();
    } catch (error) {
      console.error("Error approving worker:", error);
      toast.error("Kunne ikke godkjenne arbeider");
    } finally {
      setApproving(null);
    }
  };

  const handleSignContract = async () => {
    if (!selectedContract) return;
    
    setSigning(true);
    try {
      await axios.post(
        `${API_URL}/api/employment-contracts/${selectedContract.contract_id}/hr-sign`,
        { signature_image: signatureData },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Kontrakt signert!");
      setSignDialogOpen(false);
      setSelectedContract(null);
      setSignatureData(null);
      fetchData();
    } catch (error) {
      console.error("Error signing contract:", error);
      toast.error(error.response?.data?.detail || "Kunne ikke signere kontrakt");
    } finally {
      setSigning(false);
    }
  };

  const handleViewContractPdf = async (contractId) => {
    try {
      const response = await fetch(
        `${API_URL}/api/employment-contracts/${contractId}/pdf`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Could not load PDF");
      }
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      toast.error("Kontrakten er ikke ferdig signert ennå");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("no-NO", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  const filteredWorkers = workers.filter(worker => 
    worker.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    worker.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    worker.profile?.worker_code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: "Venter", color: "bg-yellow-500/20 text-yellow-400", icon: Clock },
      worker_signed: { label: "Venter HR", color: "bg-blue-500/20 text-blue-400", icon: Signature },
      fully_signed: { label: "Signert", color: "bg-green-500/20 text-green-400", icon: CheckCircle },
      rejected: { label: "Avvist", color: "bg-red-500/20 text-red-400", icon: XCircle }
    };
    
    const config = statusConfig[status] || { label: status, color: "bg-gray-500/20 text-gray-400", icon: Clock };
    const Icon = config.icon;
    
    return (
      <Badge className={`${config.color} border-0 flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6" data-testid="holding-workers">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Arbeidere & Kontrakter</h1>
            <p className="text-gray-400 mt-1">Administrer arbeidere og ansettelseskontrakter</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-orange-500/20 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-orange-400" />
              </div>
              <div>
                <p className="text-gray-400 text-sm">Venter Godkjenning</p>
                <p className="text-2xl font-bold text-white">{pendingApprovals.length}</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <p className="text-gray-400 text-sm">Totalt Arbeidere</p>
                <p className="text-2xl font-bold text-white">{workers.length}</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center">
                <Clock className="w-6 h-6 text-yellow-400" />
              </div>
              <div>
                <p className="text-gray-400 text-sm">Kontrakter til Signering</p>
                <p className="text-2xl font-bold text-white">{pendingContracts.length}</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <p className="text-gray-400 text-sm">Godkjente Arbeidere</p>
                <p className="text-2xl font-bold text-white">
                  {workers.filter(w => w.is_approved).length}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-[#1a1a2e] border border-[#2a2a4a]">
            <TabsTrigger 
              value="approvals" 
              className="data-[state=active]:bg-[#23F7DD] data-[state=active]:text-black"
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              Nye Søknader
              {pendingApprovals.length > 0 && (
                <Badge className="ml-2 bg-orange-500/20 text-orange-400 border-0">
                  {pendingApprovals.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger 
              value="contracts" 
              className="data-[state=active]:bg-[#23F7DD] data-[state=active]:text-black"
            >
              <FileText className="w-4 h-4 mr-2" />
              Kontrakter til Signering
              {pendingContracts.length > 0 && (
                <Badge className="ml-2 bg-yellow-500/20 text-yellow-400 border-0">
                  {pendingContracts.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger 
              value="workers"
              className="data-[state=active]:bg-[#23F7DD] data-[state=active]:text-black"
            >
              <Users className="w-4 h-4 mr-2" />
              Alle Arbeidere
            </TabsTrigger>
          </TabsList>

          {/* Pending Approvals Tab (NEW) */}
          <TabsContent value="approvals" className="space-y-4">
            {loading ? (
              <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
                <CardContent className="p-6 text-center text-gray-400">
                  <div className="w-8 h-8 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                  Laster søknader...
                </CardContent>
              </Card>
            ) : pendingApprovals.length === 0 ? (
              <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
                <CardContent className="p-8 text-center">
                  <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-400 opacity-50" />
                  <h3 className="text-white text-lg font-medium mb-2">Ingen ventende søknader</h3>
                  <p className="text-gray-400">Alle arbeidere og teamledere er godkjent</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {pendingApprovals.map((worker) => (
                  <Card key={worker.user_id} className="bg-[#1a1a2e] border-[#2a2a4a] border-l-4 border-l-orange-500">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                            worker.role === "team_leader" 
                              ? "bg-purple-500/20" 
                              : "bg-blue-500/20"
                          }`}>
                            <User className={`w-6 h-6 ${
                              worker.role === "team_leader" 
                                ? "text-purple-400" 
                                : "text-blue-400"
                            }`} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="text-white font-semibold text-lg">{worker.name}</h3>
                              <Badge className={`border-0 ${
                                worker.role === "team_leader"
                                  ? "bg-purple-500/20 text-purple-400"
                                  : "bg-blue-500/20 text-blue-400"
                              }`}>
                                {worker.role === "team_leader" ? "Team Leader" : "Worker"}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-4 mt-2 text-sm text-gray-400">
                              <span className="flex items-center gap-1">
                                <Mail className="w-4 h-4" />
                                {worker.email}
                              </span>
                              {worker.profile?.worker_code && (
                                <span className="flex items-center gap-1 font-mono text-[#23F7DD]">
                                  {worker.profile.worker_code}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-4 mt-2 text-sm text-gray-400">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                Registrert: {formatDate(worker.created_at)}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Button
                            onClick={() => handleApproveWorker(worker.user_id, worker.name)}
                            disabled={approving === worker.user_id}
                            className="bg-green-500 hover:bg-green-600 text-white"
                          >
                            {approving === worker.user_id ? (
                              <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                Godkjenner...
                              </>
                            ) : (
                              <>
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Godkjenn
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Pending Contracts Tab */}
          <TabsContent value="contracts" className="space-y-4">
            {loading ? (
              <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
                <CardContent className="p-6 text-center text-gray-400">
                  <div className="w-8 h-8 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                  Laster kontrakter...
                </CardContent>
              </Card>
            ) : pendingContracts.length === 0 ? (
              <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
                <CardContent className="p-8 text-center">
                  <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-400 opacity-50" />
                  <h3 className="text-white text-lg font-medium mb-2">Ingen kontrakter venter</h3>
                  <p className="text-gray-400">Alle ansettelseskontrakter er signert</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {pendingContracts.map((contract) => (
                  <Card key={contract.contract_id} className="bg-[#1a1a2e] border-[#2a2a4a]">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-full bg-[#23F7DD]/20 flex items-center justify-center">
                            <User className="w-6 h-6 text-[#23F7DD]" />
                          </div>
                          <div>
                            <h3 className="text-white font-semibold text-lg">{contract.worker_name}</h3>
                            <div className="flex items-center gap-4 mt-2 text-sm text-gray-400">
                              <span className="flex items-center gap-1">
                                <Mail className="w-4 h-4" />
                                {contract.worker_email}
                              </span>
                              {contract.worker_phone && (
                                <span className="flex items-center gap-1">
                                  <Phone className="w-4 h-4" />
                                  {contract.worker_phone}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-4 mt-2">
                              {getStatusBadge(contract.status)}
                              <span className="text-gray-500 text-sm">
                                Opprettet: {formatDate(contract.created_at)}
                              </span>
                            </div>
                            {contract.worker_signed && (
                              <div className="mt-2 text-sm text-green-400 flex items-center gap-1">
                                <CheckCircle className="w-4 h-4" />
                                Arbeider signerte: {formatDate(contract.worker_signed_at)}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-[#2a2a4a] hover:border-[#23F7DD]"
                            onClick={() => handleViewContractPdf(contract.contract_id)}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Se Kontrakt
                          </Button>
                          {contract.worker_signed && !contract.hr_signed && (
                            <Button
                              size="sm"
                              className="bg-[#23F7DD] hover:bg-[#1de9cf] text-black"
                              onClick={() => {
                                setSelectedContract(contract);
                                setSignDialogOpen(true);
                              }}
                            >
                              <Signature className="w-4 h-4 mr-1" />
                              Signer
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* All Workers Tab */}
          <TabsContent value="workers" className="space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                placeholder="Søk etter navn, e-post eller kode..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-[#1a1a2e] border-[#2a2a4a] text-white"
              />
            </div>

            {loading ? (
              <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
                <CardContent className="p-6 text-center text-gray-400">
                  <div className="w-8 h-8 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                  Laster arbeidere...
                </CardContent>
              </Card>
            ) : filteredWorkers.length === 0 ? (
              <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
                <CardContent className="p-8 text-center">
                  <Users className="w-16 h-16 mx-auto mb-4 text-gray-600" />
                  <h3 className="text-white text-lg font-medium mb-2">Ingen arbeidere funnet</h3>
                  <p className="text-gray-400">
                    {searchTerm ? "Prøv et annet søkeord" : "Ingen arbeidere registrert ennå"}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {filteredWorkers.map((worker) => (
                  <Card key={worker.user_id} className="bg-[#1a1a2e] border-[#2a2a4a]">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-full bg-[#23F7DD]/20 flex items-center justify-center">
                            {worker.picture ? (
                              <img src={worker.picture} alt={worker.name} className="w-12 h-12 rounded-full object-cover" />
                            ) : (
                              <User className="w-6 h-6 text-[#23F7DD]" />
                            )}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="text-white font-medium">{worker.name}</h3>
                              <Badge className={`border-0 ${worker.is_approved ? "bg-green-500/20 text-green-400" : "bg-yellow-500/20 text-yellow-400"}`}>
                                {worker.is_approved ? "Godkjent" : "Venter"}
                              </Badge>
                              {worker.role === "team_leader" && (
                                <Badge className="bg-purple-500/20 text-purple-400 border-0">
                                  Team Leader
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-4 mt-1 text-sm text-gray-400">
                              <span>{worker.email}</span>
                              {worker.profile?.worker_code && (
                                <span className="text-[#23F7DD]">{worker.profile.worker_code}</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          {worker.profile?.contract_signed ? (
                            <span className="flex items-center gap-1 text-green-400 text-sm">
                              <CheckCircle className="w-4 h-4" />
                              Kontrakt Signert
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-yellow-400 text-sm">
                              <Clock className="w-4 h-4" />
                              Venter Kontrakt
                            </span>
                          )}
                          {!worker.is_approved && (
                            <Button
                              size="sm"
                              className="bg-[#23F7DD] hover:bg-[#1de9cf] text-black"
                              onClick={() => handleApproveWorker(worker.user_id, worker.name)}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Godkjenn
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Sign Contract Dialog */}
        <Dialog open={signDialogOpen} onOpenChange={setSignDialogOpen}>
          <DialogContent className="bg-[#1a1a2e] border-[#2a2a4a] text-white max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Signature className="w-5 h-5 text-[#23F7DD]" />
                Signer Ansettelseskontrakt
              </DialogTitle>
            </DialogHeader>
            
            {selectedContract && (
              <div className="space-y-4">
                <div className="p-4 bg-[#2a2a4a]/50 rounded-lg">
                  <p className="text-sm text-gray-400">Arbeider</p>
                  <p className="text-white font-medium">{selectedContract.worker_name}</p>
                  <p className="text-gray-400 text-sm">{selectedContract.worker_email}</p>
                </div>
                
                <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-yellow-400 font-medium">Bekreftelse</p>
                      <p className="text-sm text-gray-400 mt-1">
                        Ved å signere bekrefter du at ansettelseskontrakten er gjennomgått og godkjent på vegne av Doserne Stillas AS.
                      </p>
                    </div>
                  </div>
                </div>
                
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setSignDialogOpen(false)}
                    className="border-[#2a2a4a]"
                  >
                    Avbryt
                  </Button>
                  <Button
                    onClick={handleSignContract}
                    disabled={signing}
                    className="bg-[#23F7DD] hover:bg-[#1de9cf] text-black"
                  >
                    {signing ? (
                      <>
                        <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin mr-2"></div>
                        Signerer...
                      </>
                    ) : (
                      <>
                        <Signature className="w-4 h-4 mr-2" />
                        Signer Kontrakt
                      </>
                    )}
                  </Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
